import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { loanActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { RefreshEvents } from '../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import { CommonLoanSummary } from '../common/LoanComponents';
import { formatDateTime } from '@apply/ui';
import { LoanStatusOptions, LostLoanTypeOptions, NoticeTypeOptions, LoanType, NoticeStatusType } from '@apply/constants';
import { LoanStatusType } from '../common/LoanConstants';
import Accordion from 'devextreme-react/accordion';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { CommonLoanSummaryInvokingFeatureType } from '../common/constants/CommonLoanSummaryInvokingFeatureType';

const styles = {
    buttonMarginRight: { marginRight: '10px' },
    buttonContainer: {
        textAlign: 'right',
        borderTop: '2px solid silver',
        padding: '10px 10px',
    },
    form: { padding: '10px' },
    formRow: { marginBottom: '0' },
    infoSection: {
        backgroundColor: '#c0c0c07a',
        display: 'inline-block',
        width: '100%',
        padding: '5px',
        marginTop: '5px',
        marginBottom: '10px',
    },
};

const RestoreLoanModal = () => {
    const { isOpen, loanId } = useSelector((state) => state.loan.restoreLoanModal);
    const { dispatch } = store;

    const [loan, set_loan] = useState(null);

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const _handleClose = () =>
        dispatch(
            loanActions.setRestoreLoanModal({
                restoreLoanModal: { isOpen: false },
            })
        );

    const onSubmit = async (e) => {
        e.preventDefault();

        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            let response = await loanApi.restoreLoan(loanId);
            if (response.statusCode !== 200) {
                return;
            }

            var newStatus = response.result.statusName;

            refreshEvents.dispatchRefreshEvent();
            handleAdminSuccess(`Loan restored successfully to ${newStatus} status.`);

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    useEffect(() => {
        async function loanIdChanged() {
            if (loanId && loanId > 0) {
                await loadLoanInfo(loanId);
            } else {
                resetState();
            }
            set_loadingOpen(false);
        }

        loanIdChanged();
    }, [isOpen, loanId]);

    const resetState = () => {
        set_loan(null);
    };

    const loadLoanInfo = async (id) => {
        set_loadingMessage('Loading...');
        set_loadingOpen(true);

        const response = await loanApi.getLoanSummary(id);
        if (response.statusCode !== 200) {
            set_loadingOpen(false);
            return;
        }
        const loan = response.result;
        set_loan(loan);
        set_loadingOpen(false);
    };

    const getNoticeStyle = () => {
        switch (loan.noticeStatusId) {
            case NoticeStatusType.Emailed:
            case NoticeStatusType.Generated:
                return { paddingTop: '8px', color: 'green' };
            case NoticeStatusType.Pending:
                return { paddingTop: '8px', color: 'darkred' };
            case NoticeStatusType.Error:
                return { paddingTop: '8px', color: 'darkred' };
            default:
                return { paddingTop: '8px', color: 'gray' };
        }
    };

    const getNoticeTitle = () => {
        var noticeTypeName = NoticeTypeOptions.find((f) => f.id === loan.noticeTypeId)?.name;
        switch (loan.noticeStatusId) {
            case NoticeStatusType.Emailed:
                return `The notice '${noticeTypeName}' was sent at ${formatDateTime(loan.NoticeDate)}`;
            case NoticeStatusType.Generated:
                return `The notice '${noticeTypeName}' has been generated but not yet emailed`;
            case NoticeStatusType.Pending:
                return `The notice '${noticeTypeName}' to be sent at ${formatDateTime(loan.NoticeDate)} will be cancelled`;
            case NoticeStatusType.Error:
                return `The notice '${noticeTypeName}' experiences an Error`;
            default:
                return 'No Notice has been sent or scheduled';
        }
    };

    const getReasons = (reasons, title) => {
        if (!reasons || reasons.length === 0) {
            return [
                {
                    title,
                    reasons: ['No reasons recorded'],
                },
            ];
        }
        return [
            {
                title,
                reasons,
            },
        ];
    };

    const onTitleRender = (e) => {
        return <span style={{ fontSize: 'smaller' }}>{e.title}</span>;
    };

    const onReasonsRender = (e) => {
        const items = e.reasons.map((r, index) => {
            if (r != null) {
                return <li key={'reason' + index}>{r}</li>;
            } else {
                return <li key={'reason' + index}> None Specified</li>;
            }
        });
        return <ul style={{ marginBottom: '0' }}>{items}</ul>;
    };

    if (!isOpen || !loan) {
        return '';
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '700px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form
                                    name='conversionForm'
                                    onSubmit={(event) => onSubmit(event)}
                                >
                                    <div>
                                        <div
                                            className='page-header'
                                            role='banner'
                                        >
                                            <h1 style={{ whiteSpace: 'nowrap' }}>Restore Loan</h1>
                                            <button
                                                type='button'
                                                className='close'
                                                data-dismiss='modal'
                                                aria-label='Close'
                                                onClick={_handleClose}
                                            >
                                                <span aria-hidden='true'>&times;</span>
                                            </button>
                                        </div>
                                        <div>
                                            <div
                                                style={styles.form}
                                                className={'standard-form'}
                                            >
                                                {loan.loanTypeId !== LoanType.MasterNote && (
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={getNoticeStyle()}
                                                        >
                                                            <i title={getNoticeTitle()}>
                                                                <FontAwesomeIcon icon={faPencil} />
                                                            </i>
                                                            &nbsp;&nbsp;
                                                            {getNoticeTitle()}
                                                        </div>
                                                    </div>
                                                )}

                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div className='columns small-12 large-12'>
                                                        <div
                                                            style={{
                                                                padding: '10px',
                                                                color: 'darkblue',
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faInfoCircle} />
                                                            &nbsp;&nbsp;This action will restore this loan to it's previous state.
                                                        </div>
                                                    </div>
                                                </div>

                                                <div style={styles.infoSection}>
                                                    <CommonLoanSummary
                                                        loan={loan}
                                                        invokingFeature={CommonLoanSummaryInvokingFeatureType.RestoreLoan}
                                                    />
                                                    {loan?.statusId && (
                                                        <div>
                                                            <div
                                                                className='row'
                                                                style={styles.formRow}
                                                            >
                                                                <div
                                                                    className='columns small-12 large-12'
                                                                    style={styles.formColumn}
                                                                >
                                                                    <h2 className='decorated'>Status</h2>
                                                                    <b>
                                                                        {
                                                                            LoanStatusOptions.find((a) => a.id === loan.statusId)
                                                                                ?.name
                                                                        }
                                                                    </b>
                                                                    {loan.statusId === LoanStatusType.Lost &&
                                                                        loan.lostTypeId !== null &&
                                                                        loan.lostTypeId !== undefined && (
                                                                            <b>
                                                                                {' '}
                                                                                -{' '}
                                                                                {
                                                                                    LostLoanTypeOptions.find(
                                                                                        (o) => o.id === loan.lostTypeId
                                                                                    )?.name
                                                                                }{' '}
                                                                            </b>
                                                                        )}
                                                                </div>
                                                            </div>
                                                            {loan.lostReasons !== null &&
                                                                loan.lostReasons !== undefined &&
                                                                loan.lostReasons.length > 0 && (
                                                                    <div
                                                                        className='row'
                                                                        style={styles.formRow}
                                                                    >
                                                                        <div
                                                                            className='columns small-12 large-12'
                                                                            style={styles.formColumn}
                                                                        >
                                                                            <Accordion
                                                                                dataSource={getReasons(
                                                                                    loan.lostReasons,
                                                                                    'Reasons for Loss'
                                                                                )}
                                                                                collapsible={false}
                                                                                itemTitleRender={onTitleRender}
                                                                                itemRender={onReasonsRender}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            className='button-footer'
                                            style={styles.buttonContainer}
                                        >
                                            <button
                                                style={styles.buttonMarginRight}
                                                type='submit'
                                                className='button primary'
                                            >
                                                Restore
                                            </button>
                                            <button
                                                type='button'
                                                className='button hollow'
                                                data-dismiss='modal'
                                                onClick={_handleClose}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </>
    );
};

export default RestoreLoanModal;
